import React, { useEffect, useState } from 'react';
import { userService } from '../../services/userService';
import { User } from '../../types';
import { useToast } from '../../components/Toast';
import { getMediaUrl } from '../../utils/media';

type FakeUser = User & { is_online?: boolean };

const AdminFakeUsersPage: React.FC = () => {
    const { showToast } = useToast();
    const [fakeUsers, setFakeUsers] = useState<FakeUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [onlineOnly, setOnlineOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Create modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createName, setCreateName] = useState('');
    const [creating, setCreating] = useState(false);

    // Edit modal state
    const [editingUser, setEditingUser] = useState<FakeUser | null>(null);
    const [editName, setEditName] = useState('');
    const [editAvatarFile, setEditAvatarFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);

    // Bulk selection
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [deletingBulk, setDeletingBulk] = useState(false);

    // Settings state
    const [fakeUsersEnabled, setFakeUsersEnabled] = useState(false);
    const [totalFakeUsers, setTotalFakeUsers] = useState(0);
    const [config, setConfig] = useState({
        new_users_per_hour_min: 1,
        new_users_per_hour_max: 3,
        target_online_percent: 30,
    });
    const [savingConfig, setSavingConfig] = useState(false);
    const [loadingSettings, setLoadingSettings] = useState(false);

    const fetchFakeUsers = async () => {
        setLoading(true);
        try {
            const data = await userService.listFakeUsers({
                page,
                per_page: 20,
                q: searchQuery || undefined,
                online_only: onlineOnly || undefined,
            });
            setFakeUsers(data.fake_users);
            setTotalPages(data.pages);
            setTotal(data.total);
        } catch (err: any) {
            showToast(err?.response?.data?.error || 'Không thể tải danh sách', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        setLoadingSettings(true);
        try {
            const [settingData, configData] = await Promise.all([
                userService.getFakeUsersSetting(),
                userService.getFakeUsersConfig(),
            ]);
            setFakeUsersEnabled(settingData.fake_users_enabled);
            setTotalFakeUsers(settingData.total_fake_users);
            setConfig(configData);
        } catch (err: any) {
            console.error('Error fetching settings:', err);
        } finally {
            setLoadingSettings(false);
        }
    };

    useEffect(() => {
        fetchFakeUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, onlineOnly]);

    useEffect(() => {
        fetchSettings();
    }, []);

    const handleSearch = () => {
        setPage(1);
        fetchFakeUsers();
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!createName.trim()) return;
        setCreating(true);
        try {
            await userService.createFakeUser({ full_name: createName.trim() });
            showToast('Tạo user ảo thành công', 'success');
            setShowCreateModal(false);
            setCreateName('');
            fetchFakeUsers();
            fetchSettings();
        } catch (err: any) {
            showToast(err?.response?.data?.error || 'Không thể tạo user ảo', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleEdit = (user: FakeUser) => {
        setEditingUser(user);
        setEditName(user.full_name || '');
        setEditAvatarFile(null);
    };

    const handleSaveEdit = async () => {
        if (!editingUser) return;
        setSaving(true);
        try {
            let updatedUser = editingUser;

            if (editName.trim() !== editingUser.full_name) {
                const result = await userService.updateFakeUser(editingUser.id, { full_name: editName.trim() });
                updatedUser = result.user;
            }

            if (editAvatarFile) {
                const result = await userService.uploadFakeUserAvatar(editingUser.id, editAvatarFile);
                updatedUser = result.user;
            }

            setFakeUsers(list => list.map(u => u.id === editingUser.id ? { ...updatedUser, is_online: u.is_online } : u));
            showToast('Cập nhật thành công', 'success');
            setEditingUser(null);
        } catch (err: any) {
            showToast(err?.response?.data?.error || 'Không thể cập nhật', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (user: FakeUser) => {
        if (!window.confirm(`Xóa user ảo "${user.full_name || user.username}"?`)) return;
        try {
            await userService.deleteFakeUser(user.id);
            showToast('Đã xóa user ảo', 'success');
            setFakeUsers(list => list.filter(u => u.id !== user.id));
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.delete(user.id);
                return next;
            });
            fetchSettings();
        } catch (err: any) {
            showToast(err?.response?.data?.error || 'Không thể xóa', 'error');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Xóa ${selectedIds.size} user ảo đã chọn?`)) return;
        setDeletingBulk(true);
        try {
            const result = await userService.bulkDeleteFakeUsers(Array.from(selectedIds));
            showToast(`Đã xóa ${result.deleted_count} user ảo`, 'success');
            setSelectedIds(new Set());
            fetchFakeUsers();
            fetchSettings();
        } catch (err: any) {
            showToast(err?.response?.data?.error || 'Không thể xóa', 'error');
        } finally {
            setDeletingBulk(false);
        }
    };

    const handleToggleSelect = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectedIds.size === fakeUsers.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(fakeUsers.map(u => u.id)));
        }
    };

    const handleToggleFakeUsers = async () => {
        const newValue = !fakeUsersEnabled;
        setLoadingSettings(true);
        try {
            await userService.toggleFakeUsers(newValue);
            setFakeUsersEnabled(newValue);
            showToast(newValue ? 'Đã bật tự động tạo user ảo' : 'Đã tắt tự động tạo user ảo', 'success');
        } catch (err: any) {
            showToast(err?.response?.data?.error || 'Không thể cập nhật', 'error');
        } finally {
            setLoadingSettings(false);
        }
    };

    const handleSaveConfig = async () => {
        setSavingConfig(true);
        try {
            await userService.updateFakeUsersConfig(config);
            showToast('Đã lưu cài đặt', 'success');
        } catch (err: any) {
            showToast(err?.response?.data?.error || 'Không thể lưu cài đặt', 'error');
        } finally {
            setSavingConfig(false);
        }
    };

    const handleToggleOnline = async (user: FakeUser) => {
        try {
            await userService.updateFakeUser(user.id, { is_online: !user.is_online });
            setFakeUsers(list => list.map(u => u.id === user.id ? { ...u, is_online: !u.is_online } : u));
            showToast(user.is_online ? 'Đã đánh dấu offline' : 'Đã đánh dấu online', 'success');
        } catch (err: any) {
            showToast(err?.response?.data?.error || 'Không thể cập nhật', 'error');
        }
    };

    const onlineCount = fakeUsers.filter(u => u.is_online).length;

    return (
        <>
            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Thêm User Ảo</h3>
                        </div>
                        <form onSubmit={handleCreate} className="p-6 space-y-4">
                            <label className="block">
                                <span className="text-sm font-medium text-gray-700">Họ tên <span className="text-red-500">*</span></span>
                                <input
                                    type="text"
                                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    value={createName}
                                    onChange={e => setCreateName(e.target.value)}
                                    placeholder="Nhập họ tên"
                                    required
                                />
                            </label>
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                                >
                                    Hủy
                                </button>
                                <button
                                    type="submit"
                                    disabled={creating}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {creating ? 'Đang tạo...' : 'Tạo'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {editingUser && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingUser(null)}>
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Sửa User Ảo</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="flex items-center gap-4">
                                {getMediaUrl(editingUser.avatar) ? (
                                    <img src={getMediaUrl(editingUser.avatar)} alt="" className="w-16 h-16 rounded-full object-cover" />
                                ) : (
                                    <div className="w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center text-xl font-medium text-gray-700">
                                        {editingUser.username.charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1">
                                    <p className="text-sm text-gray-500">@{editingUser.username}</p>
                                    <p className="text-xs text-gray-400">{editingUser.email}</p>
                                </div>
                            </div>
                            <label className="block">
                                <span className="text-sm font-medium text-gray-700">Họ tên</span>
                                <input
                                    type="text"
                                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    value={editName}
                                    onChange={e => setEditName(e.target.value)}
                                />
                            </label>
                            <label className="block">
                                <span className="text-sm font-medium text-gray-700">Avatar mới</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="mt-1 w-full text-sm border border-dashed border-gray-300 rounded-lg p-2"
                                    onChange={e => setEditAvatarFile(e.target.files?.[0] || null)}
                                />
                            </label>
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setEditingUser(null)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    disabled={saving}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {saving ? 'Đang lưu...' : 'Lưu'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-900">Quản lý User Ảo</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Tổng: <span className="font-medium">{totalFakeUsers}</span> user ảo
                                {onlineCount > 0 && <span className="text-green-600 ml-2">• {onlineCount} online</span>}
                            </p>
                        </div>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
                        >
                            <span className="text-lg">+</span>
                            Thêm User Ảo
                        </button>
                    </div>
                </div>

                {/* Settings Panel */}
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Auto Toggle */}
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-gray-900">Tự động tạo User Ảo</h3>
                                    <p className="text-sm text-gray-500 mt-1">Tự động thêm user ảo mỗi giờ</p>
                                </div>
                                <button
                                    onClick={handleToggleFakeUsers}
                                    disabled={loadingSettings}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${fakeUsersEnabled ? 'bg-blue-600' : 'bg-gray-200'
                                        }`}
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${fakeUsersEnabled ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Config */}
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <h3 className="font-semibold text-gray-900 mb-3">Cài đặt nâng cao</h3>
                            <div className="grid grid-cols-3 gap-3">
                                <label className="block">
                                    <span className="text-xs text-gray-500">Tối thiểu/giờ</span>
                                    <input
                                        type="number"
                                        min={0}
                                        max={10}
                                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                                        value={config.new_users_per_hour_min}
                                        onChange={e => setConfig(c => ({ ...c, new_users_per_hour_min: parseInt(e.target.value) || 0 }))}
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs text-gray-500">Tối đa/giờ</span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={10}
                                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                                        value={config.new_users_per_hour_max}
                                        onChange={e => setConfig(c => ({ ...c, new_users_per_hour_max: parseInt(e.target.value) || 1 }))}
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs text-gray-500">% Online</span>
                                    <input
                                        type="number"
                                        min={5}
                                        max={80}
                                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                                        value={config.target_online_percent}
                                        onChange={e => setConfig(c => ({ ...c, target_online_percent: parseInt(e.target.value) || 30 }))}
                                    />
                                </label>
                            </div>
                            <button
                                onClick={handleSaveConfig}
                                disabled={savingConfig}
                                className="mt-3 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                            >
                                {savingConfig ? 'Đang lưu...' : 'Lưu cài đặt'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="p-6 border-b border-gray-200 bg-white">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <input
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                placeholder="Tìm theo tên, username, email..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={onlineOnly}
                                onChange={e => { setOnlineOnly(e.target.checked); setPage(1); }}
                                className="w-4 h-4 text-blue-600 rounded"
                            />
                            <span className="text-sm text-gray-700">Chỉ online</span>
                        </label>
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                        >
                            Tìm kiếm
                        </button>
                        {selectedIds.size > 0 && (
                            <button
                                onClick={handleBulkDelete}
                                disabled={deletingBulk}
                                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                                {deletingBulk ? 'Đang xóa...' : `Xóa ${selectedIds.size} đã chọn`}
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        checked={fakeUsers.length > 0 && selectedIds.size === fakeUsers.length}
                                        onChange={handleSelectAll}
                                        className="w-4 h-4 text-blue-600 rounded"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thông tin</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày tạo</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex items-center justify-center gap-2">
                                            <span className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                                            Đang tải...
                                        </div>
                                    </td>
                                </tr>
                            ) : fakeUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                                        <p className="font-medium">Không có user ảo nào</p>
                                        <p className="text-sm text-gray-400">Bấm "Thêm User Ảo" để tạo mới</p>
                                    </td>
                                </tr>
                            ) : (
                                fakeUsers.map(user => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(user.id)}
                                                onChange={() => handleToggleSelect(user.id)}
                                                className="w-4 h-4 text-blue-600 rounded"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                {getMediaUrl(user.avatar) ? (
                                                    <img src={getMediaUrl(user.avatar)} alt="" className="w-10 h-10 rounded-full object-cover" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-medium">
                                                        {user.username.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{user.full_name || 'Chưa đặt tên'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{user.username}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => handleToggleOnline(user)}
                                                className={`px-2.5 py-1 rounded-full text-xs font-medium ${user.is_online
                                                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {user.is_online ? '● Online' : '○ Offline'}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {user.created_at ? new Date(user.created_at).toLocaleDateString('vi-VN') : '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    onClick={() => handleEdit(user)}
                                                    className="px-2.5 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                                                >
                                                    Sửa
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(user)}
                                                    className="px-2.5 py-1 rounded text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200"
                                                >
                                                    Xóa
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                            Trang {page} / {totalPages} ({total} user ảo)
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Trước
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Sau
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AdminFakeUsersPage;
